<template>
	<div
		ref="wrapper"
		class="full-page-layout"
		:class="{
			'is-desktop': !isMobileWebApp,
			'is-mobile': isMobileWebApp,
			'is-landscape': isMobileLandscape,
			'is-unfolded-foldable-phone': isUnfoldedFoldablePhone,
			'is-fullscreen': isFullscreen,
			'is-cinema-mode': isCinemaMode
		}"
	>
		<!-- mobile -->
		<div
			v-if="isMobileWebApp"
			class="full-page-layout__inner-w"
			:class="{ 'has-low-opacity': showQualityMenu }"
		>
			<div
				id="layoutMediaContainer"
				ref="mediaContainer"
				class="full-page-layout__media"
				:style="mediaStyle"
			>
				<slot name="media" />
			</div>
			<!-- meta -->
			<slot
				v-if="!isMobileLandscape"
				name="meta"
			/>
			<!-- participants -->
			<div
				v-if="!isFullscreen && !isMobileLandscape"
				class="full-page-layout__participants"
			>
				<slot name="participants" />
			</div>
			<div v-if="hasEventBannerAd && !isFullscreen && !isMobileLandscape">
				<slot name="ads" />
			</div>
			<!-- description -->
			<div
				v-if="(event.description || hasEventNotes) && !isFullscreen && !isMobileLandscape"
				class="full-page-layout__description"
			>
				<slot
					v-if="event.description"
					name="description"
				/>
				<slot
					v-if="hasEventNotes"
					name="notes"
				/>
			</div>
			<div
				class="full-page-layout__actions"
				data-testid="ad-actions"
			>
				<slot name="actions" />
			</div>
		</div>
		<!-- default-->
		<div
			v-else
			class="full-page-layout__inner-w"
		>
			<!-- left -->
			<div class="full-page-layout__left">
				<!-- player -->
				<div
					ref="mediaContainer"
					class="full-page-layout__media"
					:style="mediaStyle"
				>
					<slot name="media" />
				</div>
				<!-- timeline -->
				<slot
					v-if="!isCinemaMode"
					name="timeline"
				/>
				<!-- meta -->
				<slot
					v-if="!isCinemaMode && !isFullscreen && !isMobileLandscape"
					name="meta"
				/>
				<!-- participants -->
				<div
					v-if="!isCinemaMode && !isFullscreen && !isMobileLandscape"
					class="full-page-layout__participants"
				>
					<slot name="participants" />
				</div>
				<div v-if="hasEventBannerAd && !isCinemaMode && !isFullscreen && !isMobileLandscape">
					<slot name="ads" />
				</div>
				<!-- description -->
				<div
					v-if="!isCinemaMode && (event.description || hasEventNotes) && !isFullscreen && !isMobileLandscape"
					class="full-page-layout__description"
				>
					<slot
						v-if="event.description"
						name="description"
					/>
					<slot
						v-if="hasEventNotes"
						name="notes"
					/>
				</div>
				<div
					v-if="isCinemaMode"
					class="full-page-layout__cinema-mode-bottom"
				>
					<div class="full-page-layout__bottom-left">
						<!-- meta -->
						<slot
							v-if="!isFullscreen && !isMobileLandscape"
							name="meta"
						/>
						<!-- participants -->
						<div
							v-if="!isFullscreen && !isMobileLandscape"
							class="full-page-layout__participants"
						>
							<slot name="participants" />
						</div>
						<div v-if="hasEventBannerAd && !isFullscreen && !isMobileLandscape">
							<slot name="ads" />
						</div>
						<!-- description -->
						<div
							v-if="(event.description || hasEventNotes) && !isFullscreen && !isMobileLandscape"
							class="full-page-layout__description"
						>
							<slot
								v-if="event.description"
								name="description"
							/>
							<slot
								v-if="hasEventNotes"
								name="notes"
							/>
						</div>
					</div>
					<div
						v-if="!isFullscreen && isCinemaMode"
						v-show="!isCouch"
						class="full-page-layout__bottom-right full-page-layout__actions"
					>
						<div class="full-page-layout__actions-container">
							<slot name="actions" />
						</div>
					</div>
				</div>
			</div>
			<!-- Right column (actions) -->
			<div
				v-if="!isFullscreen && !isCinemaMode"
				v-show="!isCouch"
				class="full-page-layout__actions"
				data-testid="ad-actions"
			>
				<div class="full-page-layout__actions-container">
					<slot name="actions" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AdType } from '@shared/enums/AdType';
import { EVENT, bus } from '@videotools/js/events';
import toggleFullscreen from '../../util/toggleFullscreen';

const events = [EVENT.FULLSCREEN_TOGGLE];
const MEDIA_DISTANCE_TOP = 64 + 24;
export const LAYOUT_MODE_DEFAULT = 'default';
export const LAYOUT_MODE_CINEMA = 'cinema';

export default {
	name:  'FullPageLayout',
	props: {
		isCouch:         { type: Boolean },
		showQualityMenu: { type: Boolean },
		isCinemaMode:    { type: Boolean },
		hasEventNotes:   { type: Boolean, default: false },
	},
	data() {
		return {
			windowHeight: 0,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:      'app/isMobileWebApp',
			isFullscreen:        'device/isFullscreen',
			isMobileAppleDevice: 'device/isMobileAppleDevice',
			isMobileLandscape:   'device/isMobileLandscape',
			isMobilePortrait:    'device/isMobilePortrait',
			isLandscape:         'device/isLandscape',
			event:               'event/event',
			ads:                 'event/ads',
			playStatus:          'player/playStatus',
		}),
		isUnfoldedFoldablePhone() {
			return this.isLandscape && this.isMobilePortrait;
		},
		mediaStyle() {
			let maxHeight = 'none';
			const mediaContainerDistanceTop = this.$refs.mediaContainerxx?.getBoundingClientRect().top ?? MEDIA_DISTANCE_TOP;
			const additionalSpacingBelow = 24; // for symmetry with the top spacing
			if (!this.isFullscreen && !this.isMobileWebApp && this.windowHeight > mediaContainerDistanceTop) {
				maxHeight = `${this.windowHeight - mediaContainerDistanceTop - additionalSpacingBelow}px`;
			}

			return {
				maxHeight,
			};
		},
		hasEventBannerAd() {
			return this.ads?.find(ad => ad.type === AdType.EVENT_BANNER);
		},
	},
	mounted() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}

		if (!this.isMobileWebApp) {
			this.updateWindowHeight();
			window.addEventListener('resize', this.updateWindowHeight);
		}
	},
	destroyed() {
		// bus
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}

		if (!this.isMobileWebApp) {
			window.removeEventListener('resize', this.updateWindowHeight);
		}
	},
	methods: {
		[`BUS-${EVENT.FULLSCREEN_TOGGLE}`]() {
			if (this.isMobileAppleDevice) return; // handled by Player
			const fullscreenElement = this.isUnfoldedFoldablePhone
				? this.$el.querySelector('.js-player-wrapper') || this.$root.$el
				: this.$root.$el;
			toggleFullscreen(fullscreenElement);
		},
		updateWindowHeight() {
			this.windowHeight = window.innerHeight;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.full-page-layout {
	height: 100%;

	&.is-mobile {
		width: 100%;
		display: flex;
		flex-direction: column;
	}
}

.full-page-layout__inner-w {
	display: flex;
	height: 100%;

	.is-mobile & {
		max-width: 100%;
		width: 100%;
		flex: 1 1 100%;
		flex-direction: column;

		&.has-low-opacity {
			opacity: 0.4;
		}
	}

	.is-mobile.is-landscape & {
		flex-direction: row;
	}

	.is-desktop & {
		@include size(sm-down) {
			flex-wrap: wrap;
		}
	}

	.is-cinema-mode & {
		display: block;

		&::v-deep {
			.infinite-scroll-container {
				overflow-y: hidden;
			}
		}
	}
}

.full-page-layout__left {
	.is-fullscreen & {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.is-desktop & {
		flex: 1;
		padding-bottom: 128px;
		overflow: hidden;

		@include size(sm-down) {
			overflow: visible;
			padding-bottom: 16px;
		}
	}

	.is-desktop.is-fullscreen & {
		padding-bottom: 0;
	}

	.is-cinema-mode & {
		padding-bottom: 0;
		display: flex;
		flex-direction: column;
		overflow: initial;
	}
}

.full-page-layout__participants {
	overflow-x: auto;

	.is-desktop & {
		border-bottom: 1px solid var(--color-grey-900);
	}
}

.full-page-layout__media {
	display: flex;

	.is-fullscreen & {
		height: 100%;
	}

	.is-desktop & {
		flex: 1 1 0;
		position: relative;
		overflow: hidden;
	}

	.is-mobile & {
		width: 100%;
		position: sticky;
		top: 0;
		// the Tab component (div.container-tabs) has position relative
		// and thus it will go on top of the player when scrolling, unless we force a z-index in here
		z-index: 2;
	}

	.is-mobile.is-unfolded-foldable-phone & {
		position: static;
	}

	.is-mobile.is-landscape & {
		margin: 0;
		height: 100%;
	}

	.is-cinema-mode & {
		flex: 0 1 auto;
	}
}

.full-page-layout__actions {
	.is-desktop & {
		display: flex;
		position: relative;
		width: 388px;
		box-sizing: border-box;
		padding-bottom: 64px;

		@include size(sm-down) {
			width: 100%;
		}
	}

	.is-mobile.is-landscape & {
		display: none;
	}
}

.full-page-layout__actions-container {
	.is-desktop & {
		flex: 1;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		position: absolute;

		@include size(sm-down) {
			position: relative;
		}
	}

	.is-cinema-mode & {
		position: initial;
	}
}

.full-page-layout__description {
	padding: 24px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 400;
	color: var(--color-grey-200);
	word-break: break-word;

	.is-mobile & {
		@include text-sm-2;
		padding: 16px;
		border-bottom: 1px solid var(--color-grey-900);
	}
}

// Cinema mode
.full-page-layout__cinema-mode-bottom {
	display: flex;
	flex: 1 1 auto;

	@include size(sm-down) {
		flex-wrap: wrap;
	}
}

.full-page-layout__bottom-left {
	flex: 1;
	overflow: hidden;

	@include size(sm-down) {
		overflow: visible;
	}
}

.full-page-layout__bottom-right {
	margin-top: 24px;
}
</style>
